import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Switch } from '../ui/switch';
import { CheckCircle, Key, X } from 'lucide-react';
import { getBackendUrl } from '../../utils/api';

const API = `${getBackendUrl()}/api`;

const EvolutionAPIConfigSection = () => {
  const [config, setConfig] = useState({
    evolution_api_url: '',
    evolution_api_key: '',
    enabled: true,
    api_type: 'evolution' // 'evolution' ou 'waha'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [maskedKey, setMaskedKey] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/evolution-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.configured) {
        setConfigured(true);
        setConfig(prev => ({
          ...prev,
          evolution_api_url: response.data.evolution_api_url,
          enabled: response.data.enabled,
          api_type: response.data.api_type || 'evolution'
        }));
        setMaskedKey(response.data.evolution_api_key_masked);
      }
    } catch (err) {
      console.error('Error loading WhatsApp API config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      if (!config.evolution_api_url) {
        toast.error('URL da API é obrigatória');
        return;
      }

      await axios.put(`${API}/master/evolution-config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const apiName = config.api_type === 'waha' ? 'WAHA' : config.api_type === 'uazapi' ? 'UazAPI' : 'Evolution API';
      toast.success(`✅ Configuração ${apiName} salva com sucesso!`);
      setConfig(prev => ({
        ...prev,
        evolution_api_key: '',
      }));
      setShowKey(false);

      setTimeout(() => loadConfig(), 1000);
    } catch (err) {
      console.error('Error saving WhatsApp API config:', err);
      toast.error(err.response?.data?.detail || '❌ Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="animate-pulse h-32 bg-gray-800/50 rounded-lg"></div>;

  const isWaha = config.api_type === 'waha';
  const isUazapi = config.api_type === 'uazapi';
  const apiName = isWaha ? 'WAHA' : isUazapi ? 'UazAPI' : 'Evolution API';
  const apiColor = isWaha ? 'blue' : isUazapi ? 'purple' : 'green';

  return (
    <div className="glass-card space-y-4">
      <div>
        <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
          <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          WhatsApp API ({apiName})
        </h2>
        <p className="text-gray-400 text-sm">
          Configure as credenciais para integração WhatsApp. Escolha entre Evolution API, WAHA ou UazAPI.
        </p>
      </div>

      {/* API Type Selector */}
      <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Tipo de API WhatsApp
        </label>
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => setConfig({...config, api_type: 'evolution'})}
            className={`p-4 rounded-lg border-2 transition-all ${
              !isWaha && !isUazapi
                ? 'border-green-500 bg-green-500/10 text-green-300' 
                : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
            }`}
          >
            <div className="flex flex-col items-center gap-2">
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <span className="font-semibold text-sm">Evolution API</span>
              <span className="text-xs opacity-70">Open Source</span>
            </div>
          </button>
          <button
            onClick={() => setConfig({...config, api_type: 'waha'})}
            className={`p-4 rounded-lg border-2 transition-all ${
              isWaha 
                ? 'border-blue-500 bg-blue-500/10 text-blue-300' 
                : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
            }`}
          >
            <div className="flex flex-col items-center gap-2">
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM6 10h2v2H6zm0 4h8v2H6zm10 0h2v2h-2zm-6-4h8v2h-8z"/>
              </svg>
              <span className="font-semibold">WAHA</span>
              <span className="text-xs opacity-70">WhatsApp HTTP API</span>
            </div>
          </button>
          <button
            onClick={() => setConfig({...config, api_type: 'uazapi'})}
            className={`p-4 rounded-lg border-2 transition-all ${
              isUazapi 
                ? 'border-purple-500 bg-purple-500/10 text-purple-300' 
                : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
            }`}
          >
            <div className="flex flex-col items-center gap-2">
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
              <span className="font-semibold text-sm">UazAPI</span>
              <span className="text-xs opacity-70">Cloud BR</span>
            </div>
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          {isWaha 
            ? '💡 WAHA: API estável com dashboard integrado. Requer Docker.' 
            : isUazapi
            ? '💡 UazAPI: API brasileira cloud. Sem necessidade de servidor próprio.'
            : '💡 Evolution API: Popular e open source. Suporta múltiplas instâncias.'}
        </p>
      </div>

      {configured && (
        <div className={`${isWaha ? 'bg-blue-500/10 border-blue-500/30' : isUazapi ? 'bg-purple-500/10 border-purple-500/30' : 'bg-green-500/10 border-green-500/30'} border rounded-lg p-4`}>
          <div className="flex items-center gap-3">
            <CheckCircle className={`w-5 h-5 ${isWaha ? 'text-blue-400' : isUazapi ? 'text-purple-400' : 'text-green-400'} flex-shrink-0`} />
            <div>
              <p className={`${isWaha ? 'text-blue-300' : isUazapi ? 'text-purple-300' : 'text-green-300'} font-semibold`}>{apiName} Configurada</p>
              <p className={`${isWaha ? 'text-blue-400/80' : isUazapi ? 'text-purple-400/80' : 'text-green-400/80'} text-sm`}>Lojistas podem conectar suas instâncias</p>
            </div>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          URL da {apiName}
        </label>
        <input
          type="text"
          value={config.evolution_api_url}
          onChange={(e) => setConfig({...config, evolution_api_url: e.target.value})}
          placeholder={isWaha ? "https://seu-waha.com" : isUazapi ? "https://api.uazapi.dev" : "https://sua-evolution-api.com"}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">
          {isWaha 
            ? 'URL do servidor WAHA (sem barra no final). Ex: https://waha.seusite.com' 
            : isUazapi
            ? 'URL da API UazAPI. Ex: https://api.uazapi.dev'
            : 'URL base da sua instância Evolution API (sem barra no final)'}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {isWaha ? 'X-Api-Key' : isUazapi ? 'Bearer Token' : 'API Key Global'}
        </label>
        {configured && !showKey ? (
          <div className="flex gap-2">
            <div className="flex-1 bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2">
              <span className="text-gray-400 text-sm font-mono">{maskedKey}</span>
            </div>
            <button
              onClick={() => setShowKey(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all"
            >
              Atualizar
            </button>
          </div>
        ) : (
          <input
            type="text"
            value={config.evolution_api_key}
            onChange={(e) => setConfig({...config, evolution_api_key: e.target.value})}
            placeholder={isWaha ? "Sua X-Api-Key do WAHA" : isUazapi ? "Seu token de acesso UazAPI" : "Sua API Key global da Evolution"}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
          />
        )}
        <p className="text-xs text-gray-500 mt-1">
          {isWaha 
            ? 'Chave configurada na variável WHATSAPP_API_KEY do WAHA' 
            : isUazapi
            ? 'Token Bearer obtido no painel UazAPI'
            : 'API Key mestra para gerenciar todas as instâncias'}
        </p>
      </div>

      <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700">
        <div>
          <label className="text-white font-medium">Ativar {apiName}</label>
          <p className="text-gray-400 text-sm">Permitir que lojistas conectem WhatsApp</p>
        </div>
        <Switch
          checked={config.enabled}
          onCheckedChange={(checked) => setConfig({...config, enabled: checked})}
        />
      </div>

      <div className={`${isWaha ? 'bg-blue-500/10 border-blue-500/30' : isUazapi ? 'bg-purple-500/10 border-purple-500/30' : 'bg-green-500/10 border-green-500/30'} border rounded-lg p-4`}>
        <p className={`${isWaha ? 'text-blue-300' : isUazapi ? 'text-purple-300' : 'text-green-300'} text-sm`}>
          {isWaha ? (
            <>
              ℹ️ <strong>WAHA:</strong> Configure o webhook global no WAHA apontando para: <code className="bg-gray-800 px-2 py-1 rounded text-xs">{window.location.origin}/api/webhook/whatsapp</code>
            </>
          ) : isUazapi ? (
            <>
              ℹ️ <strong>UazAPI:</strong> Configure o webhook de cada instância para: <code className="bg-gray-800 px-2 py-1 rounded text-xs">{window.location.origin}/api/webhook/whatsapp</code>
            </>
          ) : (
            <>
              ℹ️ <strong>Como funciona:</strong> Você configura a Evolution API uma vez aqui, e cada lojista poderá conectar sua própria instância WhatsApp através do painel deles, sem precisar de credenciais adicionais.
            </>
          )}
        </p>
      </div>

      <button
        onClick={handleSave}
        disabled={saving || !config.evolution_api_url}
        className={`w-full ${isWaha 
          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600' 
          : isUazapi
          ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
          : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
        } text-white font-bold py-3 px-6 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2`}
      >
        {saving ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            Salvando...
          </>
        ) : (
          <>
            <CheckCircle className="w-5 h-5" />
            Salvar {apiName}
          </>
        )}
      </button>
    </div>
  );
};

export default EvolutionAPIConfigSection;
